/**
 * Transient notifications.
 *
 * Confirmations of things the user just did ("preview deleted"), not error reporting:
 * errors belong on the page they happened on, where they can be read and acted upon. A
 * toast that disappears is the wrong place for a Kubernetes message.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

let nextId = 1

export const useToastStore = defineStore('toasts', () => {
  /** @type {import('vue').Ref<Array<{id: number, tone: string, message: string}>>} */
  const items = ref([])

  /**
   * @param {string} message
   * @param {{tone?: 'success'|'error'|'info', timeout?: number}} [options]
   */
  function push(message, { tone = 'info', timeout = 5000 } = {}) {
    const id = nextId++
    items.value.push({ id, tone, message })
    if (timeout > 0) setTimeout(() => dismiss(id), timeout)
    return id
  }

  const success = (message, options) => push(message, { ...options, tone: 'success' })
  const info = (message, options) => push(message, { ...options, tone: 'info' })
  const error = (message, options) => push(message, { ...options, tone: 'error', timeout: 8000 })

  /** @param {number} id */
  function dismiss(id) {
    items.value = items.value.filter((item) => item.id !== id)
  }

  function clear() {
    items.value = []
  }

  return { items, push, success, info, error, dismiss, clear }
})
